import { buildDimensionFilters } from "./AlertListFilterPayload";
import { checkErrors, getDefaultOptions } from "./Category";
import { GenericDjangoPaginatedResponse } from "./utils";
import { GridType, UhCat } from "../Calendar/interfaces";
import { Filter } from "./Alert";

const EMPTY_PLACEHOLDER = "<EMPTY>";

export interface PriceUpdate {
  id: number;
  uh_cat: UhCat;
  grid_type: GridType;
  occupation_date: string;
  old_price_gross: number;
  old_price_net: number;
  new_price_gross: number;
  new_price_net: number;
  status: PriceUpdateStatus;
  updated_at: string;
  type: string;
  closure_violation?: ClosureViolation | null;
  connector_response?: Array<{
    success?: number;
    message?: string;
    name?: string;
    status?: string;
  }> | null;
}

export enum PriceUpdateStatus {
  CLOSED = "closed",
  ERROR = "error",
  NOT_SENT = "not_sent",
  OUT_OF_RANGE = "out_of_range",
  PENDING = "pending",
  PROCESSED = "processed",
  PROCESSING = "processing",
  REJECTED = "rejected",
  STALE = "stale",
  UNMAPPED = "unmapped",
  VALIDATED = "validated",
}

/**
 * Snapshot of the violated bound on a PriceUpdate row when its status
 * is OUT_OF_RANGE. Mirrors the backend `BoundViolation` TypedDict in
 * `api/prices/services/price_validation.py` — keep these in sync.
 *
 * `floor` / `ceiling` are Decimal-as-string, or null when no rule
 * applied to that side. `rule_id` is null when the binding bound came
 * from the fully-null global default rule. `rule_scope` reflects the
 * narrowest set field on the binding rule. `date_range.from` / `.to`
 * are ISO date strings or null (undated rule).
 */
/**
 * Snapshot of the closure that blocked a price when its status is CLOSED.
 * Mirrors the backend `ClosureViolation` TypedDict in
 * `api/prices/services/price_validation.py` — keep these in sync.
 *
 * `start_date` / `end_date` are inclusive ISO dates.
 */
export type ClosureViolation = {
  closure_id: number;
  start_date: string;
  end_date: string;
};

export type BoundViolation = {
  violation: "below_floor" | "above_ceiling";
  floor: string | null;
  ceiling: string | null;
  rule_id: number | null;
  rule_scope: "uh_cat" | "uh_type" | "property" | "global";
  date_range: { from: string | null; to: string | null };
};

/**
 * For update, we can only change the new_price_net
 */
export interface PriceUpdateUpdate {
  id: number;
  new_price_net: number;
}

/**
 * Workflow error count
 */
export interface WorkflowUpdateErrorCount {
  error_count: number;
}

/**
 * How the price bounds apply to a submission (`PriceUpdate.Rule` server-side).
 * SMOOTHEN is only accepted on a reference price.
 */
export enum BoundRule {
  DEFAULT = "default",
  FORCE = "force",
  SMOOTHEN = "smoothen",
}

/**
 * Calendar page price update
 */
export interface NewPriceType {
  grid_type: number;
  date_occupation: string;
  price_after_taxes: number;
  uh_cat?: number;
  // A row of `uh_type_nandc`, which is not the `uh_type` table: the ref-price
  // endpoint resolves it against a category's own `uh_type_nandc`.
  uh_type_nandc?: number;
  property?: number;
  is_pending?: boolean;
  rule?: BoundRule;
}

/**
 * Floor and ceiling one cell is checked against; null sides are not bound.
 * `hit_*` say whether the buy ups computed from `price_for_buy_up` cross a
 * bound; null when no such price was given.
 */
export interface ResolvedBounds {
  floor: number | null;
  ceiling: number | null;
  hit_floor: boolean | null;
  hit_ceiling: boolean | null;
}

/**
 * Either one `uh_cat`, or the reference of `property` (and `uh_type_nandc`).
 * `price_for_buy_up` only applies to a reference uh cat.
 */
export interface ResolveBoundsParams {
  date: string;
  grid_type: number;
  uh_cat?: number;
  property?: number;
  uh_type_nandc?: number;
  price_for_buy_up?: number;
}

export const resolvePriceBounds = async (
  params: ResolveBoundsParams
): Promise<ResolvedBounds> => {
  const url = new URL("/api/price-bounds-rules/resolve/", window.origin);
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) {
      url.searchParams.append(key, String(value));
    }
  }

  const response = await fetch(url.toString(), getDefaultOptions());

  checkErrors(response);

  return response.json();
};

/**
 * Reco price data
 */
export interface RecoPriceUpdateType {
  is_pending: boolean;
  price_after_taxes: number | undefined;
}

export type GenericObject<
  Value = any,
  Key extends string | number | symbol = string
> = Record<Key, Value>;

/**
 * Executes a fetch request
 *
 * Default to GET method but overridable with `options` similar
 * to `fetch` parameters.
 *
 * TODO: add the type for options parameter
 *
 * @param baseUrl
 * @param params
 * @param options
 */
export const basicFetch = async <
  T,
  U extends GenericObject | undefined = undefined
>(
  baseUrl: string,
  params?: U,
  options: object = {}
): Promise<GenericDjangoPaginatedResponse<T>> => {
  const url = new URL(baseUrl, window.origin);

  if (params) {
    const search: URLSearchParams = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      const value = Array.isArray(v) ? v.join(",") : v;

      if (value === EMPTY_PLACEHOLDER) {
        // Append empty string to represent empty value
        search.append(k, "");
        continue;
      }
      // Does not append null or undefined because it's an absence of param
      if (value) {
        search.append(k, value);
      }
    }
    url.search = search.toString();
  }

  const response = await fetch(url.toString(), getDefaultOptions(options));

  checkErrors(response);

  return response.json();
};

export const listPriceUpdates = async (params: any, filters?: Filter[]) => {
  const scope = filters ? buildDimensionFilters(filters, 2) : {};

  const options = {
    method: "POST",
    body: JSON.stringify({ scope }),
  };

  return basicFetch<PriceUpdate>(
    "/api/price-updates/grouped/",
    params,
    options
  );
};

export const updatePrice = async (price: PriceUpdateUpdate) => {
  const url = new URL(`/api/price-updates/${price.id}/`, window.origin);

  const response = await fetch(
    url.toString(),
    getDefaultOptions({
      method: "PUT",
      body: JSON.stringify(price),
    })
  );

  checkErrors(response);

  return response.json();
};

export const createPrice = async (price: PriceUpdateUpdate | NewPriceType) => {
  const url = new URL(`/api/prices/`, window.origin);

  const response = await fetch(
    url.toString(),
    getDefaultOptions({
      method: "POST",
      body: JSON.stringify(price),
    })
  );

  checkErrors(response);

  return response.json();
};

export const retryPrice = async (price: number) => {
  const url = new URL(`/api/price-updates/retry/`, window.origin);
  const body = { price: price };

  const response = await fetch(
    url.toString(),
    getDefaultOptions({
      method: "POST",
      body: JSON.stringify(body),
    })
  );

  checkErrors(response);

  return response.json();
};

export const retryPriceBulk = async (prices: number[]) => {
  const url = new URL(`/api/price-updates/bulk-retry/`, window.origin);
  const body = { prices: prices };

  const response = await fetch(
    url.toString(),
    getDefaultOptions({
      method: "POST",
      body: JSON.stringify(body),
    })
  );

  checkErrors(response);

  return response.json();
};

export const createPriceRef = async (
  price: PriceUpdateUpdate | NewPriceType
) => {
  const url = new URL(`/api/prices/ref/`, window.origin);

  const response = await fetch(
    url.toString(),
    getDefaultOptions({
      method: "POST",
      body: JSON.stringify(price),
    })
  );

  checkErrors(response);

  return response.json();
};

export const validatePrices = async (prices: number[]) => {
  const url = new URL("/api/price-updates/bulk-validate/", window.origin);
  const body = { prices: prices };

  const response = await fetch(
    url.toString(),
    getDefaultOptions({
      method: "POST",
      body: JSON.stringify(body),
    })
  );

  checkErrors(response);

  return response.json();
};

export const rejectPrices = async (prices: number[]) => {
  const url = new URL("/api/price-updates/bulk-reject/", window.origin);
  const body = { prices: prices };

  const response = await fetch(
    url.toString(),
    getDefaultOptions({
      method: "POST",
      body: JSON.stringify(body),
    })
  );

  checkErrors(response);

  return response.json();
};

// TODO: remove this function and use the payload one
export const acceptReco = async (id: number, reco: RecoPriceUpdateType) =>
  basicFetch("/api/nancie-recos/accept/", undefined, {
    method: "POST",
    body: JSON.stringify(reco),
  });

export const acceptRecoWithPayload = async (params: GenericObject) =>
  basicFetch("/api/nancie-recos/accept/", undefined, {
    method: "POST",
    body: JSON.stringify(params),
  });

export const rejectReco = async (id: number, reco: RecoPriceUpdateType) => {
  const url = new URL(`/api/alerts/${id}/reject/`, window.origin);

  const response = await fetch(
    url.toString(),
    getDefaultOptions({
      method: "POST",
      body: JSON.stringify(reco),
    })
  );

  checkErrors(response);

  return response.json();
};

export const rejectRecoWithPayload = async (params: GenericObject) =>
  basicFetch("/api/nancie-recos/reject/", undefined, {
    method: "POST",
    body: JSON.stringify(params),
  });

// Re-activates a decided alert (legacy user response -1); does not undo the
// price changes already applied by the decision.
export const activateRecoWithPayload = async (params: GenericObject) =>
  basicFetch("/api/nancie-recos/activate/", undefined, {
    method: "POST",
    body: JSON.stringify(params),
  });
